import React from 'react';

/**
 * Where a document came from.
 *
 * Provenance was already being stored and never shown: documents arriving from
 * bilag.accountrix.dk have carried `source: "inbound_email"` (plus sender and a
 * sender_verified flag) since email intake shipped, and nothing in the UI displayed any of it.
 * Inbox ingest adds two more origins, so this fixes the gap once for every source rather than
 * only for the new ones.
 *
 * It matters for review: an accountant judging a document should know whether the client
 * uploaded it deliberately, whether it arrived by unverified email, or whether we pulled it out
 * of the client's own accounting system without anyone choosing to send it.
 */
export const SOURCE_LABELS = {
    upload: { label: 'Uploadet', title: 'Uploaded in Accountrix by the client or the accountant' },
    inbound_email: { label: 'E-mail', title: 'Forwarded to bilag.accountrix.dk (sender not verified)' },
    inbox_uniconta: { label: 'Uniconta-indbakke', title: 'Pulled from the client’s own Uniconta voucher inbox' },
    inbox_economic: { label: 'e-conomic-indbakke', title: 'Pulled from the client’s own e-conomic scan inbox' },
};

const TONE = {
    upload: 'bg-slate-100 text-slate-700 border-slate-200',
    inbound_email: 'bg-amber-50 text-amber-800 border-amber-200',
    inbox_uniconta: 'bg-sky-50 text-sky-800 border-sky-200',
    inbox_economic: 'bg-sky-50 text-sky-800 border-sky-200',
};

export default function SourceBadge({ source, className = '' }) {
    // A document with no `source` predates provenance tracking; it is almost certainly an upload,
    // but saying so would be a guess. Show nothing rather than assert something we do not know.
    if (!source) return null;
    const meta = SOURCE_LABELS[source];
    if (!meta) return null;
    return (
        <span
            title={meta.title}
            className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none ${TONE[source] || TONE.upload} ${className}`}
        >
            {meta.label}
        </span>
    );
}
